import { useState } from "react";
import { useLocation } from "react-router-dom";
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    MessageBar,
    MessageBarBody,
    Radio,
    RadioGroup,
    Spinner,
    Text,
    useId,
} from "@fluentui/react-components";
import { useUserPreferences } from "../../contexts/UserPreferencesContext";
import { needsRoleOnboarding } from "../../services/UserPreferencesService";

/** Asks users without a role to pick one. It cannot be dismissed; render it inside the Router. */
export default function RoleOnboardingDialog() {
    const { preferences, roles, status, isSettingsOpen, save } = useUserPreferences();
    const { pathname } = useLocation();
    const [selectedRole, setSelectedRole] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const descriptionId = useId("role-onboarding-description");

    const open = status === "ready" && !isSettingsOpen && needsRoleOnboarding(preferences, roles, pathname);

    const handleContinue = async () => {
        if (!selectedRole || isSaving) {
            return;
        }

        setIsSaving(true);
        setError(null);
        const result = await save({ roleName: selectedRole });
        setIsSaving(false);
        if (!result.ok) {
            setError(result.error);
        }
    };

    return (
        <Dialog open={open} modalType="alert">
            <DialogSurface aria-describedby={descriptionId}>
                <DialogBody>
                    <DialogTitle>Welcome to HowRU</DialogTitle>
                    <DialogContent className="flex flex-col gap-3">
                        <Text as="p" id={descriptionId}>
                            Select your role to get started. You can change it later in Settings.
                        </Text>
                        <RadioGroup
                            aria-labelledby={descriptionId}
                            value={selectedRole}
                            onChange={(_, data) => setSelectedRole(data.value)}
                            disabled={isSaving}
                        >
                            {roles.map((role) => (
                                <Radio key={role} value={role} label={role} />
                            ))}
                        </RadioGroup>
                        {error && (
                            <MessageBar intent="error">
                                <MessageBarBody>{error}</MessageBarBody>
                            </MessageBar>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            appearance="primary"
                            disabled={!selectedRole || isSaving}
                            icon={isSaving ? <Spinner size="tiny" /> : undefined}
                            onClick={handleContinue}
                        >
                            Continue
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}
